import 'whatwg-fetch';
import 'custom-event-polyfill';

import Auth0Lock from 'auth0-lock';
import Autofocuser from './autofocuser';
import ErrorLogger from './ErrorLogger';
import LoginView from './LoginView';
import addSubLogo from './headingLogos/logo';
import { EmailFirst } from './client/EmailFirst';
import { form } from 'formifier';
import { formErrors } from './errorMessages/formErrors';
import HidableElement from './HidableElement';
import LockWrapper from './lockWrapper/lockWrapper';
import { Renderer, AUTOFOCUS_CALLBACK } from './Renderer';
import TextContainerElement from './TextContainerElement';
import TopLevelErrorHandler from './TopLevelErrorHandler';
import topLevelErrorRenderer from './TopLevelErrorRenderer';
import txid from './txid';
import {
  TextMapping,
  VisibilityMapping,
  WrappedElement,
} from './WrappedElement';

const TXID = txid();

const QUERY_PARAM_TO_LOCK_SCREEN_MAP = {
  'sign-up': 'signUp',
  'sign-in': 'login',
};

const WRONG_PASSWORD_ERROR_MESSAGE =
  "That email and password combo isn't right. Try again or click Forgot Password to create a new one.";

const initConfiguration = () => {
  // Decode utf8 characters properly
  const parsedConfig = JSON.parse(
    decodeURIComponent(escape(window.atob(AUTH0_CONFIGURATION)))
  );

  parsedConfig.extraParams = parsedConfig.extraParams || {};

  return parsedConfig;
};

const maintenanceModeEnabled = MAINTENANCE_MODE_ENABLED === 'true';

const initOptions = (configuration) => ({
  container: 'auth0',
  auth: {
    redirectUrl: configuration.callbackURL,
    responseType: 'code',
    params: configuration.internalOptions,
  },
  configurationBaseUrl: configuration.clientConfigurationBaseUrl,
  overrides: {
    __tenant: configuration.auth0Tenant,
    __token_issuer: configuration.authorizationServer.issuer,
  },
  allowedConnections: configuration.connection
    ? [configuration.connection]
    : null,
  rememberLastLogin: false,
  languageDictionary: {
    error: {
      login: {
        'lock.invalid_email_password': WRONG_PASSWORD_ERROR_MESSAGE,
        password_change_required: WRONG_PASSWORD_ERROR_MESSAGE,
      },
    },
    forgotPasswordAction: '',
    forgotPasswordInstructions: '',
    passwordInputPlaceholder: 'Password',
    blankPasswordErrorHint: 'Please enter your password.',
    signUpTitle: 'Create Account',
    signUpLabel: 'Create Account',
    signUpSubmitLabel: 'Create Account',
    forgotPasswordSubmitLabel: 'Send Password Reset',
    loginAtLabel: 'Sign in at %s',
    loginLabel: 'Sign In',
    loginSubmitLabel: 'Sign In',
  },
  closable: false,
  defaultADUsernameFromEmailPrefix: false,
  avatar: null,
});

const getElements = (doc) => {
  const properties = {
    innerText: 'innerText',
    none: null,
    textContent: 'textContent',
  };

  // Turning off prettier so the with-ers can be on newlines
  // prettier-ignore
  return {
    appSubLogo: new WrappedElement(doc, 'appSubLogo'),
    auth0RootEmailInput:
      new WrappedElement(doc, 'auth0', 'input[type=email]')
        .withCanBeDisabled(),
    backToEmailButton:
      new WrappedElement(doc, 'backToEmailButton')
        .withVisibilityMapping(new VisibilityMapping('isBackToEmailButtonVisible')),
    backToEmailButtonText:
      new WrappedElement(doc, 'backToEmailButton', 'span')
        .withTextMapping(new TextMapping('email', properties.textContent)),
    backToSigninButton:
      new WrappedElement(doc, 'backToSignin')
        .withVisibilityMapping(new VisibilityMapping('isBackToSignInButtonVisible')),
    emailForm:
      new WrappedElement(doc, 'emailForm')
        .withVisibilityMapping(new VisibilityMapping('isEmailFormVisible')),
    errorFlash:
      new WrappedElement(doc, 'errorFlash')
        .withVisibilityMapping(new VisibilityMapping('isErrorVisible')),
    forgotPasswordButton:
      new WrappedElement(doc, 'forgotPasswordButton')
        .withVisibilityMapping(new VisibilityMapping('isForgotPasswordButtonVisible')),
    heading:
      new WrappedElement(doc, 'heading')
        .withTextMapping(new TextMapping('headingText', properties.innerText)),
    loginHintEmailDisplay:
      new WrappedElement(doc, 'loginHintEmailDisplay')
        .withTextMapping(new TextMapping('email', properties.textContent))
        .withVisibilityMapping(new VisibilityMapping('isLoginHintEmailDisplayVisible')),
    subheading:
      new WrappedElement(doc, 'subheading')
        .withTextMapping(new TextMapping('subheadingText', properties.innerText)),
    auth0WrapperDiv: new WrappedElement(doc, 'auth0')
  };
};
const autofocusInputSelectors = {
  [AUTOFOCUS_CALLBACK.email]: () => {
    let emailInput = document.getElementById('emailInput');
    return emailInput ? [emailInput] : [];
  },
  [AUTOFOCUS_CALLBACK.password]: () => {
    let passwordInputs = document.getElementsByName('password');
    return Array.from(passwordInputs);
  },
};

const errorLogger = new ErrorLogger(TXID);
const errorHandler = new TopLevelErrorHandler(
  TXID,
  topLevelErrorRenderer(
    new HidableElement(document.getElementById('mainContainer')),
    new HidableElement(document.getElementById('topLevelErrorContainer')),
    new TextContainerElement(document.getElementById('topLevelErrorCode'))
  ),
  errorLogger.logError
);

const addEventListeners = (wrappedElements, loginView, renderCallback) => {
  wrappedElements.forgotPasswordButton.addEventListener(
    'click',
    errorHandler.wrap(() => loginView.forgotPassword().then(renderCallback))
  );

  wrappedElements.backToSigninButton.addEventListener(
    'click',
    errorHandler.wrap(() => loginView.backToSignin().then(renderCallback))
  );

  wrappedElements.backToEmailButton.addEventListener(
    'click',
    errorHandler.wrap(() => loginView.backToEmail().then(renderCallback))
  );
};

const showLogin = () => {
  const config = initConfiguration();
  const options = initOptions(config);
  const lockWrapper = new LockWrapper(config, options, Auth0Lock);
  const wrappedElements = getElements(document);
  const appName = config.dict.signin.title || '';
  const shortAppName = appName.split(' (')[0];
  const loginView = LoginView(
    shortAppName,
    new EmailFirst(EMAIL_LOOKUP_URL),
    maintenanceModeEnabled,
    TXID,
    errorLogger.logError
  );

  const renderer = new Renderer(
    lockWrapper,
    loginView.backToSignin,
    wrappedElements,
    new Autofocuser(autofocusInputSelectors),
    config.auth0Domain,
    errorHandler.wrap
  );

  addSubLogo(wrappedElements.appSubLogo.element, appName, ASSET_VERSIONED_URL);

  const requestedScreen =
    QUERY_PARAM_TO_LOCK_SCREEN_MAP[config.extraParams.expected_flow];

  const renderCallback = (vs) => renderer.render(vs);

  // The following statement will execute asynchronously.
  errorHandler.invoke(() =>
    loginView
      .init(config.extraParams.login_hint, requestedScreen)
      .then(renderCallback)
  );

  const submitHandler = errorHandler.wrap(async (target) => {
    let email = target.elements.email.value;
    await loginView.onEmailFormSubmit(email).then(renderCallback);
  });

  form(formErrors, wrappedElements.emailForm.element, submitHandler);

  addEventListeners(wrappedElements, loginView, renderCallback);
};

errorHandler.invoke(showLogin);
